import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Search, Loader2, Palette } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { iconService, IconResult } from '../../services/iconService';
import { cn } from '../../lib/utils';

interface IconSearchPanelProps {
  onSelect: (icon: IconResult) => void;
  defaultStyle?: 'line' | 'solid' | 'all';
  defaultQuery?: string;
  /** External search query from parent (for unified search) */
  externalQuery?: string;
  /** Callback when search query changes (for unified search) */
  onQueryChange?: (query: string) => void;
}

const STYLE_OPTIONS = [
  { value: 'line', label: 'Line' },
  { value: 'solid', label: 'Solid' },
  { value: 'all', label: 'All' },
] as const;

export const IconSearchPanel: React.FC<IconSearchPanelProps> = ({
  onSelect,
  defaultStyle = 'line',
  defaultQuery = '',
  externalQuery,
  onQueryChange,
}) => {
  // Search state - use external query if provided
  const [internalQuery, setInternalQuery] = useState(defaultQuery);
  const searchQuery = externalQuery !== undefined ? externalQuery : internalQuery;

  const setSearchQuery = (query: string) => {
    if (onQueryChange) {
      onQueryChange(query);
    } else {
      setInternalQuery(query);
    }
  };
  const [results, setResults] = useState<IconResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedIcon, setSelectedIcon] = useState<IconResult | null>(null);

  // Filter state
  const [style, setStyle] = useState<'line' | 'solid' | 'all'>(defaultStyle);

  // Pagination
  const [totalResults, setTotalResults] = useState(0);

  // Debounce timer ref
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Search function
  const searchIcons = useCallback(async (query: string) => {
    if (!query.trim()) {
      // Load popular icons when no query
      setLoading(true);
      setError(null);
      try {
        const response = await iconService.getPopularIcons(style);
        if (response.success) {
          setResults(response.icons);
          setTotalResults(response.totalMatches);
        } else {
          setError(response.error || 'Failed to load icons');
          setResults([]);
        }
      } catch (err) {
        setError('Failed to load icons');
        setResults([]);
      } finally {
        setLoading(false);
      }
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await iconService.searchIcons(query, {
        style,
        limit: 40,
      });

      if (response.success) {
        setResults(response.icons);
        setTotalResults(response.totalMatches);
      } else {
        setError(response.error || 'Search failed');
        setResults([]);
      }
    } catch (err) {
      setError('Failed to search icons');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [style]);

  // Debounced search on query change
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      searchIcons(searchQuery);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchQuery, searchIcons]);

  // Re-search when style changes
  useEffect(() => {
    searchIcons(searchQuery);
  }, [style]);

  // Handle icon click
  const handleIconClick = (icon: IconResult) => {
    setSelectedIcon(icon);
    onSelect(icon);
  };

  // Handle search on Enter key
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      searchIcons(searchQuery);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/* Search Bar and Filters */}
      <div className="border-b border-border px-3 py-2 bg-background flex-shrink-0 space-y-2">
        {/* Row 1: Search Input */}
        <div className="flex gap-2 items-center">
          <div className="flex-1 relative">
            <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search icons (e.g., home, settings, arrow)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="pl-8 pr-3 h-8 text-sm"
              autoFocus
            />
          </div>
          <Button
            onClick={() => searchIcons(searchQuery)}
            disabled={loading}
            size="sm"
            className="h-8 px-3"
          >
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          </Button>
        </div>

        {/* Row 2: Style Filter + Results Count */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Style Filter */}
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-muted-foreground uppercase tracking-wide">Style:</span>
            <div className="flex">
              {STYLE_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => setStyle(option.value)}
                  className={cn(
                    "px-2 py-0.5 text-[10px] transition-colors border-y border-r first:border-l first:rounded-l last:rounded-r",
                    style === option.value
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background hover:bg-muted border-border"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          {/* Results Count */}
          {totalResults > 0 && (
            <div className="text-[10px] text-muted-foreground ml-auto">
              {results.length} of {totalResults.toLocaleString()} icons
            </div>
          )}
        </div>
      </div>

      {/* Results Grid */}
      <div className="flex-1 overflow-y-auto px-2 py-2 min-h-0">
        {error ? (
          <div className="text-center py-8">
            <p className="text-destructive text-sm">{error}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-muted mb-3">
              <Palette className="h-6 w-6 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground text-sm">
              {searchQuery ? 'No icons found. Try a different search term.' : 'Start typing to search icons'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-6 md:grid-cols-8 lg:grid-cols-10 xl:grid-cols-12 gap-1.5">
            {results.map((icon) => (
              <div
                key={icon.id}
                className={cn(
                  "relative group cursor-pointer rounded-lg p-2 transition-all flex flex-col items-center",
                  selectedIcon?.id === icon.id
                    ? "ring-2 ring-primary bg-primary/10"
                    : "hover:bg-muted border border-transparent hover:border-border"
                )}
                onClick={() => handleIconClick(icon)}
                title={`${icon.name} (${icon.collectionName})`}
              >
                {/* Icon Preview */}
                <div className="w-8 h-8 flex items-center justify-center">
                  <img
                    src={icon.svgUrl}
                    alt={icon.name}
                    className="w-6 h-6 object-contain"
                    loading="lazy"
                  />
                </div>

                {/* Icon Name */}
                <p className="text-[9px] text-muted-foreground text-center truncate w-full mt-1">
                  {icon.name}
                </p>

                {/* Collection badge on hover */}
                <div className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="bg-black/70 text-white text-[7px] px-1 py-0.5 rounded">
                    {icon.collection}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer hint */}
      <div className="flex-shrink-0 border-t border-border px-3 py-1.5 bg-muted/30">
        <p className="text-[10px] text-muted-foreground text-center">
          Click an icon to select, then customize color and size
        </p>
      </div>
    </div>
  );
};

export default IconSearchPanel;
